import DOMPurify from "dompurify";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DeleteBtn, MainBtn } from "../../components/button";
import { PageLoader } from "../../components/loader";
import DeleteModal from "../../components/modals/deletemodal";
import Shell from "../../components/shell";
import {
  useDeleteCourse,
  useGetCourseById,
  useGetCoursePriceById,
} from "../../hooks/useCourse";
import { toast } from "sonner";

const createMarkup = (html) => ({
  __html: DOMPurify.sanitize(html || ""),
});

const CourseDetails = () => {
  const params = useParams();
  const { data, isLoading } = useGetCourseById({ id: params.id });
  const { data: coursePriceData, isLoading: isCoursePriceLoading } =
    useGetCoursePriceById({ id: params.id });
  const [modal, setModal] = useState("");
  const navigate = useNavigate();
  const deleteCourse = useDeleteCourse({ id: params.id });

  const handleDelete = () => {
    toast.promise(deleteCourse.mutateAsync(), {
      loading: "Deleteing course...",
      success: (res) => {
        navigate("/courses");
        return "Course deleted";
      },
      error: (err) => {
        return "An error occured while deleting course.";
      },
    });
  };

  const course = !isLoading && data?.responseObject.data;
  const coursePrice =
    !isCoursePriceLoading && coursePriceData?.responseObject.data;

  const stats = [
    {
      title: "No. of Modules",
      value: course?.course_modules?.length || 0,
      icon: "📚",
    },
    { title: "Skill Level", value: course?.skillLevel, icon: "🎯" },
    { title: "Category", value: course?.category, icon: "📂" },
    { title: "Status", value: course?.status, icon: "⚡" },
  ];

  if (isLoading) {
    return <PageLoader />;
  }

  const btn = {
    isActive: true,
    title: "Go back",
    onClick: () => navigate("/courses"),
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-red-100 text-red-800";
      case "draft":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Shell
      pageHeader={course?.title}
      pageTitle="Course Details"
      btnAction={btn}
    >
      <div className="space-y-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-blue-50 via-white to-purple-50 rounded-3xl p-8 shadow-sm border border-gray-100">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/3">
              <div className="relative overflow-hidden rounded-2xl shadow-lg group">
                <img
                  src={course?.image}
                  alt={course?.title}
                  className="w-full h-64 lg:h-80 object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>

            <div className="lg:w-2/3 space-y-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                    {course?.title}
                  </h1>
                  {course?.status && (
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                        course?.status,
                      )}`}
                    >
                      {course?.status}
                    </span>
                  )}
                </div>

                <div className="bg-white/70 backdrop-blur-sm rounded-xl p-6 border border-gray-200">
                  <div className="h-[500px] overflow-auto">
                    <h4 className="text-2xl font-bold text-gray-800 mb-4">
                      Course Summary
                    </h4>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      {course?.summary}
                    </p>
                  </div>
                </div>
              </div>

              {/* Enhanced Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{stat.icon}</span>
                      <p className="text-sm font-medium text-gray-600 uppercase tracking-wide">
                        {stat.title}
                      </p>
                    </div>
                    <p className="text-xl font-bold text-gray-900">
                      {stat.value}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Course Description */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
            <h2 className="text-3xl font-bold text-gray-900">
              Course Description
            </h2>
          </div>
          <div className="bg-gray-50/50 rounded-2xl p-6 border border-gray-100">
            <div
              className="prose prose-lg max-w-none text-gray-700 max-h-[400px] overflow-y-auto custom-scrollbar"
              dangerouslySetInnerHTML={createMarkup(course?.description)}
            />
          </div>
        </div>

        {/* Pricing and Requirements */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {!isCoursePriceLoading && coursePrice && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-3xl p-8 border border-blue-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">💰</span>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Course Pricing
                  </h3>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-4xl font-bold text-blue-600">
                    NGN{" "}
                    {!isCoursePriceLoading && coursePrice === null
                      ? "Unavailable"
                      : (coursePrice?.coursePricing).toLocaleString("en-NG", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                  </p>
                </div>
                {coursePrice && (
                  <div className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-full text-sm font-medium">
                    <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
                    Available for Purchase
                  </div>
                )}
              </div>
            </div>
          )}

          {course?.course_benchmark && (
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-3xl p-8 border border-green-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full -translate-y-16 translate-x-16 opacity-50"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-3xl">🎓</span>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Course Requirements
                  </h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between bg-white/70 rounded-xl p-4">
                    <span className="text-gray-700 font-medium">
                      Passing Score:
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      {course?.course_benchmark.benchmark}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between bg-white/70 rounded-xl p-4">
                    <span className="text-gray-700 font-medium">
                      Retakes Allowed:
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      {course?.course_benchmark.retakes}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="transform hover:scale-105 transition-transform duration-200">
              <MainBtn
                text="✏️ Edit Course"
                onClick={() =>
                  navigate(
                    `/courses/edit-course?course=${params.id}&type=course&mode=edit&isPublished=${course?.isPublished}`,
                  )
                }
              />
            </div>
            <div className="transform hover:scale-105 transition-transform duration-200">
              <DeleteBtn
                text="🗑️ Delete Course"
                onClick={() => setModal("delete")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {modal === "delete" && (
        <DeleteModal
          handleCloseModal={() => setModal("")}
          title="Course"
          id={params.id}
          onClick={handleDelete}
        />
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </Shell>
  );
};

export default CourseDetails;

export const ContentWriteup = (content, cls) => {
  return (
    <>
      <div
        className={cls || "text-xs text-main satoshi"}
        dangerouslySetInnerHTML={createMarkup(content || "")}
      />
    </>
  );
};
